<template>
  <div class="app-layout">
    <!-- 顶部导航 -->
    <AppHeader />
    
    <!-- 主体内容区域 -->
    <div class="layout-container">
      <!-- 侧边导航 -->
      <AppSidebar />
      
      <!-- 页面内容 -->
      <div class="main-content">
        <div class="page-wrapper">
          <router-view />
        </div>
      </div>
    </div>
    
    <!-- 报警通知组件 -->
    <AlarmNotification ref="alarmNotificationRef" />
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, provide } from 'vue'
import { useRouter } from 'vue-router'
import AppHeader from './AppHeader.vue'
import AppSidebar from './AppSidebar.vue'
import AlarmNotification from '../AlarmNotification.vue'
import API_CONFIG from '../../config/api.js'

export default {
  name: 'AppLayout',
  components: {
    AppHeader,
    AppSidebar,
    AlarmNotification
  },
  setup() {
    const router = useRouter()
    // 轮询相关变量（已禁用，完全依赖WebSocket推送）
    // let pollingTimer = null
    // let lastCheckTime = new Date().toISOString()
    
    // 告警通知组件引用
    const alarmNotificationRef = ref(null)
    
    // 已显示的告警ID集合（防止重复显示）
    const shownAlarmIds = new Set()
    
    // 安全移除通知元素的函数
    const safeRemoveNotification = (notificationId) => {
      try {
        const element = document.getElementById(notificationId)
        if (element && element.parentNode && element.isConnected) {
          console.log('安全移除通知:', notificationId)
          element.remove()
          return true
        } else {
          console.log('通知元素不存在或已被移除:', notificationId)
          return false
        }
      } catch (error) {
        console.error('移除通知时出错:', error)
        return false
      }
    }
    
    // 将安全移除函数挂载到window对象，供内联事件使用
    window.safeRemoveNotification = safeRemoveNotification
    
    // 提供全局的报警通知方法
    const showAlarmNotification = (alarmData) => {
      const alarmId = alarmData.alarmId || alarmData.id
      console.log('准备显示告警通知:', { 
        原始数据: { alarmId: alarmData.alarmId, id: alarmData.id },
        使用的ID: alarmId,
        已显示列表: Array.from(shownAlarmIds)
      })
      
      // 检查是否已经显示过这个告警
      if (shownAlarmIds.has(alarmId)) {
        console.log('告警已显示过，跳过重复显示:', alarmId)
        return
      }
      
      console.log('显示新告警通知:', alarmId)
      
      // 记录已显示的告警ID
      shownAlarmIds.add(alarmId)
      
      // 5分钟后清除记录，避免内存泄漏
      setTimeout(() => {
        shownAlarmIds.delete(alarmId)
      }, 5 * 60 * 1000)
      
      // 优先尝试组件引用方式
      if (alarmNotificationRef.value && alarmNotificationRef.value.addAlarmNotification) {
        console.log('使用组件引用调用 addAlarmNotification')
        try {
          alarmNotificationRef.value.addAlarmNotification(alarmData)
          return
        } catch (error) {
          console.warn('组件引用调用失败:', error)
        }
      }
      
      // 备用：使用全局方法
      if (window.addAlarmNotification) {
        console.log('使用全局方法调用 window.addAlarmNotification')
        try {
          window.addAlarmNotification(alarmData)
          return
        } catch (error) {
          console.warn('全局方法调用失败:', error)
        }
      }
      
      // 最后备用方案：直接显示DOM通知
      console.log('使用备用DOM通知显示告警')
      showFallbackNotification(alarmData)
    }
    
    // 通过provide让子组件可以调用报警通知
    provide('showAlarmNotification', showAlarmNotification)
    
    // 告警级别映射函数
    const mapAlarmLevel = (levelId) => {
      const level = parseInt(levelId) || 2
      switch (level) {
        case 4: return 'CRITICAL'
        case 3: return 'WARNING'
        case 2: return 'INFO'
        case 1: return 'LOW'
        default: return 'INFO'
      }
    }
    
    // 获取告警类型颜色（与场景卡片保持一致）
    const getAlarmTypeColor = (alarmType, alarmTitle) => {
      const colorMap = {
        // 基础告警类型
        'FIRE': '#ff4d4f',
        'SMOKE_ALARM': '#ff4d4f', 
        'GAS_LEAK': '#fa8c16',
        'FALL_DETECTION': '#f5222d',
        'INTRUSION': '#722ed1',
        'DOOR_OPEN': '#722ed1',
        'WATER_LEAK': '#1890ff',
        'LOW_BATTERY': '#faad14',
        'DEVICE_OFFLINE': '#8c8c8c',
        'TEMPERATURE_HIGH': '#ff7875',
        'TEMPERATURE_LOW': '#40a9ff',
        'HUMIDITY_HIGH': '#52c41a',
        
        // 网络故障类型
        'NETWORK_DISCONNECTED': '#ff7875',
        'SIGNAL_WEAK': '#ff7875',
        'NETWORK_TIMEOUT': '#ff7875',
        'DNS_FAILURE': '#ff7875',
        
        // 硬件故障类型
        'SENSOR_FAILURE': '#d48806',
        'HARDWARE_MALFUNCTION': '#d48806',
        'POWER_SUPPLY_FAULT': '#fa541c',
        'MEMORY_ERROR': '#d48806',
        'STORAGE_FULL': '#d48806',
        
        // 环境极端条件
        'EXTREME_TEMPERATURE': '#ad2102',
        'ELECTROMAGNETIC_INTERFERENCE': '#ad2102',
        'PHYSICAL_DAMAGE': '#ad2102',
        'ENVIRONMENTAL_HAZARD': '#ad2102',
        
        // 系统级故障
        'MESSAGE_QUEUE_ERROR': '#531dab',
        'DATABASE_CONNECTION_LOST': '#531dab',
        'SERVICE_UNAVAILABLE': '#531dab',
        'SYSTEM_OVERLOAD': '#531dab',
        
        // 安全攻击模拟
        'SECURITY_BREACH': '#cf1322',
        'DATA_TAMPERING': '#cf1322',
        'UNAUTHORIZED_ACCESS': '#cf1322',
        'MALICIOUS_DEVICE': '#cf1322',
        'DDOS_ATTACK': '#cf1322',
        
        // 业务逻辑故障
        'DATA_CORRUPTION': '#389e0d',
        'CONFIGURATION_ERROR': '#389e0d',
        'SYNCHRONIZATION_FAILURE': '#389e0d',
        'BACKUP_FAILURE': '#389e0d',
        
        'DEFAULT': '#409eff'
      }
      
      // 特殊场景的颜色处理
      if (alarmTitle && alarmTitle.includes('多重故障')) {
        return '#d46b08'
      }
      
      return colorMap[alarmType] || colorMap.DEFAULT
    }

    // 获取告警类型图标（与场景卡片保持一致）
    const getAlarmTypeIcon = (alarmType, alarmTitle) => {
      const iconMap = {
        // 基础告警类型
        'FIRE': '🔥',
        'SMOKE_ALARM': '🚬', 
        'GAS_LEAK': '⛽',
        'FALL_DETECTION': '🆘',
        'INTRUSION': '🚨',
        'DOOR_OPEN': '🚪',
        'WATER_LEAK': '💧',
        'LOW_BATTERY': '🔋',
        'DEVICE_OFFLINE': '📡',
        'TEMPERATURE_HIGH': '🌡️',
        'TEMPERATURE_LOW': '❄️',
        'HUMIDITY_HIGH': '💨',
        
        // 网络故障类型
        'NETWORK_DISCONNECTED': '📡',
        'SIGNAL_WEAK': '📡',
        'NETWORK_TIMEOUT': '📡',
        'DNS_FAILURE': '📡',
        
        // 硬件故障类型
        'SENSOR_FAILURE': '⚙️',
        'HARDWARE_MALFUNCTION': '⚙️',
        'POWER_SUPPLY_FAULT': '⚡',
        'MEMORY_ERROR': '⚙️',
        'STORAGE_FULL': '⚙️',
        
        // 环境极端条件
        'EXTREME_TEMPERATURE': '🌡️',
        'ELECTROMAGNETIC_INTERFERENCE': '🌡️',
        'PHYSICAL_DAMAGE': '🌡️',
        'ENVIRONMENTAL_HAZARD': '🌡️',
        
        // 系统级故障
        'MESSAGE_QUEUE_ERROR': '💻',
        'DATABASE_CONNECTION_LOST': '💻',
        'SERVICE_UNAVAILABLE': '💻',
        'SYSTEM_OVERLOAD': '💻',
        
        // 安全攻击模拟
        'SECURITY_BREACH': '🛡️',
        'DATA_TAMPERING': '🛡️',
        'UNAUTHORIZED_ACCESS': '🛡️',
        'MALICIOUS_DEVICE': '🛡️',
        'DDOS_ATTACK': '🛡️',
        
        // 业务逻辑故障
        'DATA_CORRUPTION': '🔧',
        'CONFIGURATION_ERROR': '🔧',
        'SYNCHRONIZATION_FAILURE': '🔧',
        'BACKUP_FAILURE': '🔧',
        
        'DEFAULT': '🔔'
      }
      
      // 特殊场景的图标处理
      if (alarmTitle && alarmTitle.includes('多重故障')) {
        return '⚠️'
      }
      
      return iconMap[alarmType] || iconMap.DEFAULT
    }
    
    // 备用告警通知显示（DOM直接操作）
    const showFallbackNotification = (alarmData) => {
      console.log('使用备用DOM方式显示告警通知:', alarmData)
      
      try {
        // 简化版通知 - 确保能够显示
        const notification = document.createElement('div')
        const notificationId = 'fallback-alarm-' + Date.now()
        notification.id = notificationId
        
        const alarmColor = getAlarmTypeColor(alarmData.alarmType, alarmData.title)
        const alarmIcon = getAlarmTypeIcon(alarmData.alarmType, alarmData.title)
        
        // 设置基础样式
        notification.style.cssText = `
          position: fixed;
          top: 80px;
          right: 20px;
          width: 350px;
          background: linear-gradient(135deg, #fff 0%, ${alarmColor}12 100%);
          border-left: 4px solid ${alarmColor};
          border-radius: 8px;
          padding: 16px;
          z-index: 9999;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          font-family: Arial, sans-serif;
          font-size: 14px;
          transform: translateX(100%);
          transition: transform 0.3s ease-out;
        `
        
        // 设置内容
        notification.innerHTML = `
          <div style="display: flex; gap: 12px; align-items: flex-start;">
            <div style="
              width: 40px; 
              height: 40px; 
              background: linear-gradient(135deg, ${alarmColor}, ${alarmColor}dd); 
              border-radius: 50%; 
              display: flex; 
              align-items: center; 
              justify-content: center; 
              color: white; 
              font-size: 18px;
              flex-shrink: 0;
            ">
              ${alarmIcon}
            </div>
            <div style="flex: 1; min-width: 0;">
              <div style="font-weight: 600; color: #333; margin-bottom: 4px; font-size: 16px; line-height: 1.2;">
                ${alarmData.title || '告警通知'}
              </div>
              <div style="color: #666; font-size: 13px; margin-bottom: 2px; line-height: 1.3;">
                设备: ${alarmData.deviceName || '未知设备'}
              </div>
              <div style="color: #666; font-size: 13px; margin-bottom: 2px; line-height: 1.3;">
                位置: ${alarmData.location || '位置未知'}
              </div>
              <div style="color: #999; font-size: 13px; font-weight: 500;">
                ${new Date().toLocaleTimeString('zh-CN')}
              </div>
            </div>
            <button onclick="safeRemoveNotification('${notificationId}')" style="
              position: absolute;
              top: 8px;
              right: 8px;
              width: 24px;
              height: 24px;
              border-radius: 50%;
              background: rgba(0,0,0,0.1);
              border: none;
              color: #666;
              cursor: pointer;
              font-size: 16px;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s ease;
            " onmouseover="this.style.background='rgba(0,0,0,0.2)'" onmouseout="this.style.background='rgba(0,0,0,0.1)'">×</button>
          </div>
        `
        
        console.log('准备添加通知到DOM...')
        
        // 添加到页面
        document.body.appendChild(notification)
        console.log('通知已添加到DOM，ID:', notificationId)
        
        // 触发滑入动画
        setTimeout(() => {
          notification.style.transform = 'translateX(0)'
          console.log('滑入动画已触发')
        }, 10)
        
        // 点击事件（除了关闭按钮）
        notification.addEventListener('click', (e) => {
          if (e.target.tagName !== 'BUTTON') {
            console.log('点击通知，跳转到告警中心')
            router.push('/alarms')
            safeRemoveNotification(notificationId)
          }
        })
        
        // 10秒后自动隐藏
        setTimeout(() => {
          safeRemoveNotification(notificationId)
        }, 10000)
        
        console.log('备用告警通知设置完成，ID:', notificationId)
        
      } catch (error) {
        console.error('创建备用通知时出错:', error)
        
        // 最简单的备用方案
        alert(`告警通知: ${alarmData.title}\n设备: ${alarmData.deviceName}\n位置: ${alarmData.location}`)
      }
    }
    
    // 检查新报警（已禁用，完全依赖WebSocket推送）
    // const checkNewAlarms = async () => {
    //   try {
    //     const user = localStorage.getItem('user')
    //     const family = localStorage.getItem('family')
    //     
    //     if (!user || !family) {
    //       return
    //     }
    //     
    //     const userData = JSON.parse(user)
    //     const familyData = JSON.parse(family)
    //     const token = localStorage.getItem('token')
    //     
    //     const response = await fetch(API_CONFIG.getApiUrl('/alarms?page=1&size=5'), {
    //       headers: {
    //         'Authorization': `Bearer ${token}`,
    //         'X-User-Id': userData.id,
    //         'X-Family-Id': familyData.id,
    //         'Content-Type': 'application/json'
    //       }
    //     })
    //     
    //     if (response.ok) {
    //       const result = await response.json()
    //       if (result.success && result.data && result.data.length > 0) {
    //         let hasNewAlarms = false
    //         
    //         // 检查是否有新报警（仅用于统计，不显示弹窗）
    //         result.data.forEach(alarm => {
    //           const alarmTime = new Date(alarm.createdAt)
    //           const lastCheck = new Date(lastCheckTime)
    //           
    //           if (alarm.status === 'PENDING' && alarmTime > lastCheck) {
    //             console.log('轮询发现新报警（仅统计，不显示弹窗）:', alarm)
    //             hasNewAlarms = true
    //             
    //             // 不再显示报警弹窗，避免与WebSocket重复
    //             // 弹窗由WebSocket实时通知处理
    //           }
    //         })
    //         
    //         // 如果有新报警，立即更新badge数量
    //         if (hasNewAlarms) {
    //           updateBadgeCount()
    //         }
    //       }
    //     }
    //     
    //     // 更新最后检查时间
    //     lastCheckTime = new Date().toISOString()
    //     
    //   } catch (error) {
    //     console.error('检查新报警失败:', error)
    //   }
    // }

    // HTTP轮询通知检查（已禁用，完全依赖WebSocket推送）
    // const checkNotifications = async () => {
    //   try {
    //     const user = localStorage.getItem('user')
    //     const family = localStorage.getItem('family')
    //     
    //     if (!user || !family) {
    //       return
    //     }
    //     
    //     const userData = JSON.parse(user)
    //     const familyData = JSON.parse(family)
    //     const token = localStorage.getItem('token')
    //     
    //     // 使用新的HTTP轮询API获取通知
    //     const response = await fetch(API_CONFIG.getApiUrl('/notifications/poll?lastTimestamp=' + Date.now()), {
    //       headers: {
    //         'Authorization': `Bearer ${token}`,
    //         'X-User-Id': userData.id,
    //         'X-Family-Id': familyData.id,
    //         'Content-Type': 'application/json'
    //       }
    //     })
    //     
    //     if (response.ok) {
    //         const result = await response.json()
    //         if (result.success && result.data && result.data.notifications && result.data.notifications.length > 0) {
    //           console.log('HTTP轮询获取到新通知（仅统计，不显示弹窗）:', result.data.notifications)
    //           
    //           // 不再显示告警弹窗，避免与WebSocket重复
    //           // 弹窗由WebSocket实时通知处理
    //           // 只更新badge数量
    //           
    //           // 更新badge数量
    //           updateBadgeCount()
    //         }
    //     }
    //     
    //   } catch (error) {
    //     console.error('HTTP轮询检查通知失败:', error)
    //   }
    // }
    
    // 更新侧边栏badge数量
    const updateBadgeCount = async () => {
      try {
        if (window.updatePendingAlarmsCount) {
          const user = localStorage.getItem('user')
          const family = localStorage.getItem('family')
          
          if (user && family && family !== 'null') {
            const userData = JSON.parse(user)
            const familyData = JSON.parse(family)
            
            if (!familyData || !familyData.id) {
              console.warn('家庭信息无效，跳过HTTP轮询请求')
              return
            }
            
            const token = localStorage.getItem('token')
            const response = await fetch(API_CONFIG.getApiUrl('/alarms/pending-count'), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'X-User-Id': userData.id,
                'X-Family-Id': familyData.id,
                'Content-Type': 'application/json'
              }
            })
            
            if (response.ok) {
              const result = await response.json()
              if (result.success) {
                window.updatePendingAlarmsCount(result.data || 0)
              }
            }
          }
        }
      } catch (error) {
        console.error('更新badge数量失败:', error)
      }
    }
    
    // 获取报警级别
    const getAlarmLevel = (alarmLevelId) => {
      const levelMap = {
        '1': 'INFO',
        '2': 'WARNING', 
        '3': 'HIGH',
        '4': 'CRITICAL'
      }
      return levelMap[alarmLevelId] || 'INFO'
    }
    
    // 开始轮询（已禁用，完全依赖WebSocket推送）
    const startPolling = () => {
      console.log('HTTP轮询已禁用，系统完全依赖WebSocket推送')
      // 不再启动HTTP轮询，完全依赖WebSocket实时推送
      // checkNotifications() // 已禁用
      // checkNewAlarms() // 已禁用
      
      // 不启动定时器，避免HTTP轮询干扰WebSocket推送
      // pollingTimer = setInterval(() => {
      //   checkNotifications() // 已禁用
      //   checkNewAlarms() // 已禁用
      // }, 10000) // 已禁用
      
      console.log('系统已完全切换到WebSocket推送模式')
    }
    
    // 停止轮询（已禁用，完全依赖WebSocket推送）
    // const stopPolling = () => {
    //   if (pollingTimer) {
    //     console.log('停止轮询机制')
    //     clearInterval(pollingTimer)
    //     pollingTimer = null
    //   }
    // }
    
    onMounted(() => {
      console.log('AppLayout组件已挂载')
      
      // 等待一段时间确保AlarmNotification组件完全初始化
      setTimeout(() => {
        // 检查组件是否就绪
        const componentReady = alarmNotificationRef.value && alarmNotificationRef.value.addAlarmNotification
        const globalReady = typeof window.addAlarmNotification === 'function'
        
        console.log('告警通知组件状态:', {
          componentReady,
          globalReady,
          status: componentReady ? '组件引用就绪' : (globalReady ? '全局方法就绪' : '使用备用方案')
        })
        
        // HTTP轮询已禁用，系统完全依赖WebSocket推送
        console.log('系统已完全切换到WebSocket推送模式，不再使用HTTP轮询')
        startPolling()
        
      }, 1000) // 确保组件完全就绪
    })
    
    // 清理函数，防止内存泄漏
    onBeforeUnmount(() => {
      // HTTP轮询已禁用，无需停止轮询
      // stopPolling()
      
      // 清除已显示告警记录
      shownAlarmIds.clear()
    })
    
    return {
      alarmNotificationRef,
      showAlarmNotification
    }
  }
}
</script>

<style scoped>
.app-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
}

.layout-container {
  flex: 1;
  display: flex;
  min-height: 0; /* 重要：允许flex子项缩小 */
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* 重要：防止内容溢出 */
  position: relative; /* 为sticky定位提供定位上下文 */
}

.page-wrapper {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #f5f5f5;
  position: relative; /* 为sticky定位提供定位上下文 */
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-wrapper {
    padding: 10px;
  }
}

/* 全局样式重置 */
:deep(.el-card) {
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  width: 100%;
}

:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

/* 确保所有滚动相关元素宽度一致 */
:deep(.el-scrollbar) {
  width: 100%;
}

:deep(.el-scrollbar__wrap) {
  width: 100%;
}

:deep(.el-scrollbar__view) {
  width: 100%;
}

:deep(.el-button) {
  border-radius: 6px;
}

/* 自定义滚动条样式 */
.page-wrapper::-webkit-scrollbar {
  width: 6px;
}

.page-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.page-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.page-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>